import type { QuestionnaireResponse, SubmitAnswerItem } from "@wellcheck/api-client";

import type { AnswersDraft, SurveyCampaign, SurveySection } from "@/features/respond/data/types";

/**
 * mapQuestionnaireToCampaign — API の QuestionnaireResponse (ADR-0024) を
 * 画面表示用の SurveyCampaign (features/respond/data/types.ts) に変換する。
 *
 * backend の設問マスタは表示用途に特化していないため (id/title/description/scaleLabels/items)、
 * 既存の SurveySectionView / ReviewScreen が期待する SurveySection/Question 形へ詰め替える。
 * 現状 BJSQ は four-scale のみのため、変換先も four-scale 固定でよい。
 *
 * sections/items が null の場合 (huma の nullable array) は空配列として扱う。
 *
 * 調査票タイトルは i18next locale JSON (respond.campaignTitle) が一次ソース。
 * pure 関数のまま副作用 (t() 呼び出し) を持ち込まないよう、呼び出し側 (SurveyForm) が
 * useTranslation で解決した文字列を引数として渡す設計にする。
 */
export function mapQuestionnaireToCampaign(
  response: QuestionnaireResponse,
  title: string,
): SurveyCampaign {
  const sections: SurveySection[] = (response.sections ?? []).map((section) => ({
    id: section.id,
    title: section.title,
    description: section.description,
    scale: (section.scaleLabels ?? []).map((option) => ({
      value: option.value,
      label: option.label,
    })),
    questions: (section.items ?? []).map((item) => ({
      id: item.displayId,
      type: "four-scale" as const,
      required: true,
      label: item.questionText,
      itemNo: item.itemNo,
    })),
  }));

  return {
    id: `bjsq-${response.questionnaireType}`,
    title,
    sections,
  };
}

/**
 * mapAnswersToSubmitItems — draft の回答 (AnswersDraft, questionId=displayId キー) を
 * POST /api/respondent-answers の body (SubmitAnswerItem[], itemNo 昇順) に変換する。
 *
 * BJSQ は four-scale (数値 1..4) のみなので、文字列値は不正回答として除外する
 * (呼び出し側は全問回答済みを事前に保証している前提だが、型的な安全のため弾く)。
 */
export function mapAnswersToSubmitItems(
  campaign: SurveyCampaign,
  answers: AnswersDraft,
): SubmitAnswerItem[] {
  const items: SubmitAnswerItem[] = [];
  for (const section of campaign.sections) {
    for (const question of section.questions) {
      if (question.type !== "four-scale") continue;
      const value = answers[question.id];
      if (typeof value !== "number") continue;
      items.push({ itemNo: question.itemNo, value });
    }
  }
  return items.sort((a, b) => a.itemNo - b.itemNo);
}
